import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { formatEmvUsd } from "@/lib/format-emv"
import { cn } from "@/lib/utils"
import type { EmvMissionRow } from "@/types/emv-analytics"

/**
 * OKS-503 — Per-mission EMV roll-up table.
 *
 * Sibling of `AnalyticsTopMissionsTable` (OKS-63). Columns: Mission
 * (title + type chip), Posts (count), Total EMV (full USD), Share of
 * brand total (percent). Sorted EMV-desc (server-side). `$0` missions
 * are listed, never hidden. Mobile reflow: stacked cards with the EMV
 * value emphasized.
 */
export interface EmvMissionsTableProps {
  missions: EmvMissionRow[]
  className?: string
}

export function EmvMissionsTable({
  missions,
  className,
}: EmvMissionsTableProps) {
  const { t } = useTranslation()
  const isEmpty = missions.length === 0

  return (
    <Card
      data-testid="emv-missions-table"
      className={cn("flex flex-col gap-3 p-4 sm:p-6", className)}
    >
      <CardHeader className="p-0">
        <CardTitle className="text-foreground text-base font-semibold">
          {t("emv.missions.title")}
        </CardTitle>
      </CardHeader>

      <CardContent className="p-0">
        {isEmpty ? (
          <EmptyState />
        ) : (
          <>
            <DesktopTable missions={missions} className="hidden md:block" />
            <MobileCards missions={missions} className="md:hidden" />
          </>
        )}
      </CardContent>
    </Card>
  )
}

interface RowsProps {
  missions: EmvMissionRow[]
  className?: string
}

function DesktopTable({ missions, className }: RowsProps) {
  const { t, i18n } = useTranslation()
  return (
    <div className={className} data-testid="emv-missions-table-desktop">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("emv.missions.columns.mission")}</TableHead>
            <TableHead className="text-right">
              {t("emv.missions.columns.posts")}
            </TableHead>
            <TableHead className="text-right">
              {t("emv.missions.columns.total")}
            </TableHead>
            <TableHead className="text-right">
              {t("emv.missions.columns.share")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {missions.map((m) => (
            <TableRow key={m.id} data-testid="emv-missions-row">
              <TableCell>
                <MissionCell title={m.title} type={m.mission_type} />
              </TableCell>
              <TableCell className="text-right">{m.post_count}</TableCell>
              <TableCell className="text-right font-medium">
                {formatEmvUsd(m.total_emv_cents, i18n.language)}
              </TableCell>
              <TableCell className="text-muted-foreground text-right">
                {formatShare(m.share, i18n.language)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function MobileCards({ missions, className }: RowsProps) {
  const { t, i18n } = useTranslation()
  return (
    <ul
      className={cn("flex flex-col gap-3", className)}
      data-testid="emv-missions-table-mobile-list"
    >
      {missions.map((m) => (
        <li
          key={m.id}
          className="bg-muted/30 space-y-2 rounded-lg border p-3"
          data-testid="emv-missions-card"
        >
          <div className="flex items-start justify-between gap-2">
            <MissionCell title={m.title} type={m.mission_type} />
            <span className="text-foreground shrink-0 font-semibold">
              {formatEmvUsd(m.total_emv_cents, i18n.language)}
            </span>
          </div>
          <dl className="text-muted-foreground flex gap-4 text-xs">
            <div className="flex gap-1">
              <dt>{t("emv.missions.columns.posts")}:</dt>
              <dd className="text-foreground font-medium">{m.post_count}</dd>
            </div>
            <div className="flex gap-1">
              <dt>{t("emv.missions.columns.share")}:</dt>
              <dd className="text-foreground font-medium">
                {formatShare(m.share, i18n.language)}
              </dd>
            </div>
          </dl>
        </li>
      ))}
    </ul>
  )
}

function MissionCell({ title, type }: { title: string; type: string }) {
  const { t } = useTranslation()
  // Known mission types resolve to a localized chip; unknown values fall
  // back to the raw string (defensive — mission_type set may grow).
  const knownTypes = ["drop", "promotional", "open"]
  const typeLabel = knownTypes.includes(type)
    ? t(`emv.missions.type.${type}`)
    : type

  return (
    <div className="flex flex-col gap-1">
      <span
        className="text-foreground max-w-[24ch] truncate font-medium"
        title={title}
      >
        {title}
      </span>
      <Badge variant="secondary" className="w-fit">
        {typeLabel}
      </Badge>
    </div>
  )
}

function formatShare(share: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(share)
}

function EmptyState() {
  const { t } = useTranslation()
  return (
    <div
      data-testid="emv-missions-table-empty"
      className="text-muted-foreground flex flex-col items-center gap-2 py-8 text-sm"
    >
      <p className="text-foreground text-sm font-medium">
        {t("emv.missions.empty.title")}
      </p>
      <p>{t("emv.missions.empty.body")}</p>
    </div>
  )
}
